import './PolicyStyles.css';
import { exportToWord } from '../utils/exportToWord';

const RedundancyPolicy = ({
  clientName,
  companyNumber,
  clientAddress,
  effectiveDate,
  hrContact,
  transportManagerContact,
}) => {
  const handleExportToWord = () => {
    const policyContent = document.getElementById('policy-content');
    exportToWord(policyContent.innerHTML, "Redundancy_Policy.docx");
  };

  return (
    <div className="policy-container" id="policy-content">
      <h3 className="policy-title">Redundancy Policy</h3>

      <p className="policy-details"><strong>Company Name:</strong> {clientName || "[Insert Company Name]"}</p>
      <p className="policy-details"><strong>Company Number:</strong> {companyNumber || "[Insert Company Number]"}</p>
      <p className="policy-details"><strong>Company Address:</strong> {clientAddress || "[Insert Company Address]"}</p>
      <p className="policy-details"><strong>Policy Effective Date:</strong> {effectiveDate || "[Insert Effective Date]"}</p>

      <h4 className="section-title">1. Purpose</h4>
      <p className="section-content">
        {clientName || "The Company"} is committed to managing any redundancy situation fairly, transparently and in accordance with UK law. This policy sets out
        how we will consult with employees, apply objective selection criteria, consider suitable alternatives and provide support to anyone affected.
      </p>

      <h4 className="section-title">2. Scope</h4>
      <p className="section-content">
        This policy applies to all employees, including drivers and operational/office staff, whose roles may be affected by organisational change (e.g. contract
        loss, reduced workload, business restructure or technology changes) within our transport operations (HGV/PSV/Van).
      </p>

      <h4 className="section-title">3. Principles</h4>
      <ul>
        <li>Comply with employment legislation and ACAS guidance.</li>
        <li>Treat everyone with dignity and respect; no discrimination on protected grounds.</li>
        <li>Engage in genuine and meaningful consultation before decisions are finalised.</li>
        <li>Use clear, job‑related, objective selection criteria and keep records of decisions.</li>
        <li>Seek to avoid redundancy by exploring alternatives where reasonably practicable.</li>
      </ul>

      <h4 className="section-title">4. Consultation Process</h4>
      <p className="section-content">
        We will consult with affected employees individually and, where applicable, collectively through elected representatives. Consultation will cover the
        business reasons, proposed selection pools and criteria, possible alternatives to redundancy, and the likely timescales. Employees may be accompanied at
        formal meetings by a colleague or trade union representative. Feedback will be considered before any final decisions.
      </p>

      <h4 className="section-title">5. Selection Pools & Criteria</h4>
      <p className="section-content">
        Where fewer roles remain than employees available, we will identify a reasonable selection pool (e.g., drivers on a particular contract, depot or skill
        set). Objective criteria may include:
      </p>
      <ul>
        <li>Skills, qualifications and training relevant to current and future business needs.</li>
        <li>Performance and competence (based on documented evidence).</li>
        <li>Attendance record (excluding pregnancy‑related absence, disability‑related absence and other legally protected reasons).</li>
        <li>Disciplinary record (live warnings only).</li>
      </ul>
      <p className="section-content">
        Criteria will be applied consistently and shared with affected employees. Scoring and rationales will be documented.
      </p>

      <h4 className="section-title">6. Avoiding Redundancies & Suitable Alternative Employment</h4>
      <p className="section-content">
        We will take reasonable steps to avoid or reduce redundancies, including recruitment freezes, natural attrition, reducing overtime/agency use,
        redeployment and offering suitable alternative roles where available. Employees who accept a suitable alternative role will normally have a statutory
        4‑week trial period, extendable for training. Pay protection, relocation or retraining support may be considered case‑by‑case.
      </p>

      <h4 className="section-title">7. Notice, Redundancy Pay & Final Pay</h4>
      <p className="section-content">
        Employees selected for redundancy will receive written notice in line with contractual or statutory notice (whichever is greater), or payment in lieu
        where appropriate. Statutory redundancy pay will be provided to eligible employees based on age, length of continuous service and weekly pay (subject to
        statutory caps). Final pay will include accrued but untaken holiday and any other sums properly due, less deductions required by law.
      </p>

      <h4 className="section-title">8. Time Off to Seek Work or Training</h4>
      <p className="section-content">
        Employees under notice of redundancy who have at least two years’ continuous service are entitled to reasonable paid time off to attend interviews or
        arrange training to assist in finding new employment. Employees should give as much notice as possible of appointments.
      </p>

      <h4 className="section-title">9. Support for Affected Employees</h4>
      <p className="section-content">
        We will treat employees with empathy and provide reasonable support which may include: reference letters, CV/interview guidance, signposting to external
        services, and wellbeing support. Company property and systems access will be managed sensitively and securely during notice.
      </p>

      <h4 className="section-title">10. Appeals & Queries</h4>
      <p className="section-content">
        Employees may appeal a redundancy decision within a reasonable timeframe stated in their outcome letter. Appeals will be heard impartially by a manager not
        previously involved where possible. Queries about pay and benefits can be raised with HR or Payroll.
      </p>

      <h4 className="section-title">11. Record Keeping & Data Protection</h4>
      <p className="section-content">
        Consultation notes, scoring, letters and outcome records will be kept securely and only for as long as necessary in line with UK GDPR and Company
        retention schedules.
      </p>

      <h4 className="section-title">12. Roles and Responsibilities</h4>
      <p className="section-content">
        Managers and HR are responsible for planning and running the process, ensuring fair application of criteria and timely communication. Employees are
        responsible for engaging with consultation, providing relevant information and considering alternative roles where offered. The Transport Manager will
        be consulted on any operational impacts within transport teams.
      </p>

      <h4 className="section-title">13. Health and Safety</h4>
      <p className="section-content">
        The Company recognises that redundancy processes can be challenging. We will manage them with care and maintain a safe, respectful working environment for
        everyone involved.
      </p>

      <h4 className="section-title">Contact Information</h4>
      <ul>
        <li><strong>Human Resources:</strong> {hrContact || "[Insert Contact Number]"}</li>
        <li><strong>Transport Manager:</strong> {transportManagerContact || "[Insert Contact Number]"}</li>
      </ul>

      <h4 className="section-title">Employee Acknowledgment and Signature</h4>
      <p>I confirm that I have read and understood the Redundancy Policy and agree to its terms.</p>
      <p>Employee Name: _________________________</p>
      <p>Employee Signature: _________________________</p>
      <p>Date: _________________________</p>

      <h4 className="section-title">Management Approval and Signature</h4>
      <p>This policy has been approved by senior management and is now in effect.</p>
      <p>Manager/Officer Name: _________________________</p>
      <p>Title: _________________________</p>
      <p>Signature: _________________________</p>
      <p>Date: _________________________</p>

      <div className="mt-4 text-center">
        <button
          onClick={handleExportToWord}
          className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition"
        >
          Export to Word
        </button>
      </div>
    </div>
  );
};

export default RedundancyPolicy;